import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, Loader, X } from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';

const DriverDeliveriesPage = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');


  const token = localStorage.getItem('driverToken');


  // Fetch deliveries based on driverEmail
  useEffect(() => {
    const driverEmail = JSON.parse(localStorage.getItem('driverData'));
    fetchDeliveries(driverEmail.email);
  }, []);

  const fetchDeliveries = async (driverEmail) => {
    try {
      const response = await axios.get(`https://afric-fresh-backend.onrender.com/driver/deliveries?driverEmail=${driverEmail}`,{
        headers: {
          Authorization: `Bearer ${token}`
        }});
      setDeliveries(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      setLoading(false);
    }
  };

  // Handle order delivered
const handleOrderDelivered = async (delivery) => {
  const { value: file } = await Swal.fire({
    title: 'Upload Delivery Proof',
    input: 'file',
    inputAttributes: {
      accept: 'image/*',
      'aria-label': 'Upload your proof of delivery image',
    },
  });

  if (file) {
    // Show loading state
    Swal.fire({
      title: 'Uploading...',
      text: 'Please wait while we process your delivery confirmation',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'africfreshupload');

    try {
      // Upload image to Cloudinary
      const cloudinaryResponse = await axios.post(
        'https://api.cloudinary.com/v1_1/dfehxeb5r/image/upload',
        formData
      );

      const imageUrl = cloudinaryResponse.data.secure_url;
      const deliveredAt = new Date();

      // Mark order as delivered - CORRECTED VERSION
      await axios.patch(
        `https://afric-fresh-backend.onrender.com/driver/deliveries/${delivery._id}/complete`,
        {
          imageUrl,
          deliveredAt,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Refresh deliveries list
      const driverEmail = JSON.parse(localStorage.getItem('driverData')).email;
      fetchDeliveries(driverEmail);

      Swal.fire({
        icon: 'success',
        title: 'Order Delivered!',
        text: 'The order has been marked as delivered.',
      });

      // Close the modal
      setIsModalOpen(false);
    } catch (error) {
      console.error('Delivery confirmation error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response?.data?.message || 'Something went wrong! Please try again.',
      });
    }
  }
};

  // Filter deliveries based on search query
  const filteredDeliveries = deliveries.filter((delivery) => {
    const order = delivery.orderDetails;

    // Check if order exists and has the required properties
    if (!order || !order.orderNumber || !order.billingAddress) {
      return false; // Skip this delivery if order details are missing
    }

    // Convert orderNumber to a string for the .includes() method
    const orderNumberStr = order.orderNumber.toString();

    return (
      orderNumberStr.includes(searchQuery) || // Search by order number
      order.billingAddress.email.includes(searchQuery) || // Search by email
      order.billingAddress.phone.includes(searchQuery) // Search by phone
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Pending Deliveries</h1>

      {/* Search Bar */}
      <input
        type="text"
        placeholder="Search by Order ID, Email, or Phone"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full p-2 border border-gray-300 rounded-lg"
      />

      {/* No results message */}
      {searchQuery && filteredDeliveries.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center"
        >
          <p className="text-blue-800 font-medium">
            No deliveries found matching "{searchQuery}".
          </p>
          <p className="text-blue-600 text-sm mt-1">
            Try searching with a different order number, email, or phone number.
          </p>
        </motion.div>
      )}

      <motion.div
        layout
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
      >
        {filteredDeliveries.map((delivery) => {
          const timeSinceAccepted = Math.floor(
            (new Date() - new Date(delivery.acceptedAt)) / (1000 * 60)
          );

          return (
            <motion.div
              key={delivery._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white p-4 md:p-6 rounded-lg shadow-sm"
            >
              <div className="flex items-start space-x-4">
                <Loader className="w-6 h-6 text-amber-500 flex-shrink-0 animate-spin" />
                <div className="flex-1">
                  <p className="text-lg font-semibold text-gray-800">
                    Order #{delivery.orderDetails.orderNumber}
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <p className="text-sm text-gray-600">
                      Accepted {timeSinceAccepted} minutes ago
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <p className="text-sm text-gray-600">
                      {delivery.orderDetails.billingAddress.city}, {delivery.orderDetails.billingAddress.state}
                    </p>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-gray-700">
                    Total: ${delivery.orderDetails.total}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedDelivery(delivery);
                  setIsModalOpen(true);
                }}
                className="mt-4 w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition"
              >
                View Details
              </button>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Empty state when no deliveries at all */}
      {deliveries.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="bg-gray-100 p-4 rounded-full mb-4">
            <MapPin className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-gray-800">No Deliveries Available</h3>
          <p className="text-gray-600 mt-2 text-center max-w-md">
            You currently don't have any pending deliveries assigned to you. Check back later for new orders.
          </p>
        </div>
      )}

      {/* Modal for Order Details */}
      {isModalOpen && selectedDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[999]">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start">
              <h2 className="text-xl font-bold text-gray-800">
                Order Details #{selectedDelivery.orderDetails.orderNumber}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="mt-6 space-y-4">
              <p><strong>Accepted At:</strong> {new Date(selectedDelivery.acceptedAt).toLocaleString()}</p>
              <p><strong>Time Since Accepted:</strong> {Math.floor((new Date() - new Date(selectedDelivery.acceptedAt)) / (1000 * 60))} minutes</p>
              <p><strong>Customer Name:</strong> {selectedDelivery.orderDetails.billingAddress.firstName} {selectedDelivery.orderDetails.billingAddress.lastName}</p>
              <p><strong>Customer Email:</strong> {selectedDelivery.orderDetails.billingAddress.email}</p>
              <p><strong>Customer Phone:</strong> {selectedDelivery.orderDetails.billingAddress.phone}</p>
              <p><strong>Delivery Address:</strong> {selectedDelivery.orderDetails.billingAddress.streetHouseApartment}, {selectedDelivery.orderDetails.billingAddress.city}, {selectedDelivery.orderDetails.billingAddress.state}</p>
              <p><strong>Total Amount:</strong> ${selectedDelivery.orderDetails.total}</p>
              <p><strong>Payment Method:</strong> {selectedDelivery.orderDetails.paymentMethod}</p>
              <p><strong>Shipping Method:</strong> {selectedDelivery.orderDetails.shippingMethod}</p>
              <p><strong>Order Status:</strong> {selectedDelivery.orderDetails.orderStatus}</p>
              <p><strong>Order Type:</strong> {selectedDelivery.orderDetails.orderType}</p>
              <p><strong>Items:</strong></p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedDelivery.orderDetails.cartItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <img
                      src={item.img} // Ensure the item has an `img` property
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div>
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        ${item.price} × {item.quantity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => handleOrderDelivered(selectedDelivery)}
              className="mt-6 w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition"
            >
              Order Delivered
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDeliveriesPage;